"use strict";
/**
 * Python /api/v2/threads client — durable thread + member storage.
 * Mirrors audroam-api thread_service / Angular threadStore shapes (camelCase).
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.threadsApiServiceFactory = exports.ThreadsApiServiceImpl = void 0;
var THREADS_API_BASE = '/api/v2/threads';
function rejectMsg($q, data, fallback) {
    var message = (data === null || data === void 0 ? void 0 : data.error) || (data === null || data === void 0 ? void 0 : data.Message) || fallback;
    return $q.reject(new Error(message));
}
function httpErrorMessage(err, fallback) {
    var data = err === null || err === void 0 ? void 0 : err.data;
    if (data && typeof data === 'object') {
        return data.error || data.Message || data.message || fallback;
    }
    if ((err === null || err === void 0 ? void 0 : err.status) === 0)
        return 'Network error (threads API unreachable)';
    if ((err === null || err === void 0 ? void 0 : err.status) === 401) {
        var detail = (data && typeof data === 'object' && (data.error || data.Message)) || '';
        return detail
            ? String(detail)
            : 'Not authenticated for threads (missing or expired JWT)';
    }
    if ((err === null || err === void 0 ? void 0 : err.status) === 503)
        return 'Threads service unavailable';
    return (err === null || err === void 0 ? void 0 : err.message) || fallback;
}
function readJwtToken() {
    try {
        return localStorage.getItem('jwt_token');
    }
    catch (_a) {
        return null;
    }
}
/**
 * Explicit Authorization on every call (same pattern as session revoke).
 * Relying only on authInterceptor has failed for some $http.delete configs
 * (Flask then returns "Missing authorization header").
 */
function withAuth(config) {
    var token = readJwtToken();
    var base = config || {};
    var headers = __assign({}, (base.headers || {}));
    if (token && !headers.Authorization && !headers.authorization) {
        headers.Authorization = 'Bearer ' + token;
    }
    return __assign(__assign({}, base), { headers: headers });
}
var ThreadsApiServiceImpl = /** @class */ (function () {
    function ThreadsApiServiceImpl($http, $q) {
        this.$http = $http;
        this.$q = $q;
    }
    ThreadsApiServiceImpl.prototype.listThreads = function (opts) {
        var _this = this;
        var params = {};
        if (opts === null || opts === void 0 ? void 0 : opts.accountId)
            params.accountId = String(opts.accountId);
        if (opts === null || opts === void 0 ? void 0 : opts.status)
            params.status = String(opts.status);
        if ((opts === null || opts === void 0 ? void 0 : opts.limit) != null)
            params.limit = opts.limit;
        if (opts === null || opts === void 0 ? void 0 : opts.includeMembers)
            params.includeMembers = 1;
        return this.$http
            .get(THREADS_API_BASE, withAuth({ params: params }))
            .then(function (response) {
            var data = response.data;
            if (!data || !Array.isArray(data.threads)) {
                return rejectMsg(_this.$q, data, 'Threads list failed');
            }
            return data.threads;
        })
            .catch(function (err) { return _this.$q.reject(new Error(httpErrorMessage(err, 'Threads list failed'))); });
    };
    ThreadsApiServiceImpl.prototype.createThread = function (input) {
        var _this = this;
        var _a;
        var body = {
            title: input.title,
            kind: input.kind || 'custom',
            accountId: (_a = input.accountId) !== null && _a !== void 0 ? _a : null,
            status: input.status || 'open',
            body: input.body,
            metadata: input.metadata,
            createdBy: input.createdBy,
        };
        if (input.id)
            body.id = input.id;
        return this.$http
            .post(THREADS_API_BASE, body, withAuth())
            .then(function (response) {
            var data = response.data;
            if (!(data === null || data === void 0 ? void 0 : data.thread)) {
                return rejectMsg(_this.$q, data, 'Create thread failed');
            }
            return data.thread;
        })
            .catch(function (err) {
            // 409 = id already exists (migration race) — surface clearly
            if ((err === null || err === void 0 ? void 0 : err.status) === 409) {
                return _this.$q.reject(new Error(httpErrorMessage(err, 'Thread id already exists')));
            }
            return _this.$q.reject(new Error(httpErrorMessage(err, 'Create thread failed')));
        });
    };
    ThreadsApiServiceImpl.prototype.getThread = function (threadId) {
        var _this = this;
        if (!threadId) {
            return this.$q.reject(new Error('thread id required'));
        }
        return this.$http
            .get("".concat(THREADS_API_BASE, "/").concat(encodeURIComponent(threadId)), withAuth())
            .then(function (response) {
            var data = response.data;
            if (!(data === null || data === void 0 ? void 0 : data.thread)) {
                return rejectMsg(_this.$q, data, 'Thread not found');
            }
            return data.thread;
        })
            .catch(function (err) { return _this.$q.reject(new Error(httpErrorMessage(err, 'Thread load failed'))); });
    };
    ThreadsApiServiceImpl.prototype.updateThread = function (threadId, patch) {
        var _this = this;
        return this.$http
            .patch("".concat(THREADS_API_BASE, "/").concat(encodeURIComponent(threadId)), patch, withAuth())
            .then(function (response) {
            var data = response.data;
            if (!(data === null || data === void 0 ? void 0 : data.thread)) {
                return rejectMsg(_this.$q, data, 'Update thread failed');
            }
            return data.thread;
        })
            .catch(function (err) { return _this.$q.reject(new Error(httpErrorMessage(err, 'Update thread failed'))); });
    };
    ThreadsApiServiceImpl.prototype.deleteThread = function (threadId) {
        var _this = this;
        return this.$http
            .delete("".concat(THREADS_API_BASE, "/").concat(encodeURIComponent(threadId)), withAuth())
            .then(function (response) {
            var _a;
            if (((_a = response.data) === null || _a === void 0 ? void 0 : _a.Result) !== 'OK' && response.status !== 200) {
                return rejectMsg(_this.$q, response.data, 'Delete thread failed');
            }
        })
            .catch(function (err) { return _this.$q.reject(new Error(httpErrorMessage(err, 'Delete thread failed'))); });
    };
    ThreadsApiServiceImpl.prototype.listMembers = function (threadId) {
        var _this = this;
        return this.$http
            .get("".concat(THREADS_API_BASE, "/").concat(encodeURIComponent(threadId), "/members"), withAuth())
            .then(function (response) {
            var data = response.data;
            if (!data || !Array.isArray(data.members)) {
                return rejectMsg(_this.$q, data, 'List members failed');
            }
            return data.members;
        })
            .catch(function (err) { return _this.$q.reject(new Error(httpErrorMessage(err, 'List members failed'))); });
    };
    ThreadsApiServiceImpl.prototype.addMember = function (threadId, input) {
        var _this = this;
        var body = {
            memberId: input.memberId,
            memberType: input.memberType || 'pilot_note',
            role: input.role || 'line_item',
            status: input.status != null && String(input.status).trim() !== ''
                ? input.status
                : 'todo',
            pathHint: input.pathHint,
            linkedBy: input.linkedBy,
        };
        return this.$http
            .post("".concat(THREADS_API_BASE, "/").concat(encodeURIComponent(threadId), "/members"), body, withAuth())
            .then(function (response) {
            var data = response.data;
            if (!(data === null || data === void 0 ? void 0 : data.member)) {
                return rejectMsg(_this.$q, data, 'Add member failed');
            }
            return data.member;
        })
            .catch(function (err) {
            // Idempotent: already on server (sync re-push or double-click)
            if ((err === null || err === void 0 ? void 0 : err.status) === 409) {
                var mid = String(input.memberId);
                return {
                    threadId: threadId,
                    memberType: (input.memberType || 'pilot_note'),
                    memberId: mid,
                    role: input.role || 'line_item',
                    status: input.status || 'todo',
                    pathHint: input.pathHint,
                    sort: 0,
                    linkedAt: new Date().toISOString(),
                    linkedBy: input.linkedBy,
                    _alreadyMember: true,
                };
            }
            return _this.$q.reject(new Error(httpErrorMessage(err, 'Add member failed')));
        });
    };
    ThreadsApiServiceImpl.prototype.updateMember = function (threadId, memberId, patch) {
        var _this = this;
        var body = {};
        if (patch.status !== undefined)
            body.status = patch.status;
        if (patch.role !== undefined)
            body.role = patch.role;
        if (patch.pathHint !== undefined)
            body.pathHint = patch.pathHint;
        if (patch.memberType)
            body.memberType = patch.memberType;
        return this.$http
            .patch("".concat(THREADS_API_BASE, "/").concat(encodeURIComponent(threadId), "/members/").concat(encodeURIComponent(String(memberId))), body, withAuth())
            .then(function (response) {
            var data = response.data;
            if (!(data === null || data === void 0 ? void 0 : data.member)) {
                return rejectMsg(_this.$q, data, 'Update member failed');
            }
            return data.member;
        })
            .catch(function (err) {
            return _this.$q.reject(new Error(httpErrorMessage(err, 'Update member failed')));
        });
    };
    ThreadsApiServiceImpl.prototype.removeMember = function (threadId, memberId, memberType) {
        var _this = this;
        if (memberType === void 0) { memberType = 'pilot_note'; }
        // Prefer full $http config (method+url) so DELETE always carries Authorization
        // the same way session revoke does — shortcut delete(url, {params}) was arriving
        // at Flask without Authorization → "Missing authorization header".
        return this.$http(withAuth({
            method: 'DELETE',
            url: "".concat(THREADS_API_BASE, "/").concat(encodeURIComponent(threadId), "/members/").concat(encodeURIComponent(String(memberId))),
            params: { memberType: memberType },
        }))
            .then(function (response) {
            var _a;
            if (((_a = response.data) === null || _a === void 0 ? void 0 : _a.Result) !== 'OK' && response.status !== 200) {
                return rejectMsg(_this.$q, response.data, 'Remove member failed');
            }
        })
            .catch(function (err) { return _this.$q.reject(new Error(httpErrorMessage(err, 'Remove member failed'))); });
    };
    ThreadsApiServiceImpl.prototype.threadsForRow = function (rowId) {
        var _this = this;
        return this.$http
            .get("".concat(THREADS_API_BASE, "/for-row/").concat(encodeURIComponent(String(rowId))), withAuth())
            .then(function (response) {
            var data = response.data;
            if (!data || !Array.isArray(data.threads)) {
                return rejectMsg(_this.$q, data, 'Threads for row failed');
            }
            return data.threads;
        })
            .catch(function (err) { return _this.$q.reject(new Error(httpErrorMessage(err, 'Threads for row failed'))); });
    };
    return ThreadsApiServiceImpl;
}());
exports.ThreadsApiServiceImpl = ThreadsApiServiceImpl;
exports.threadsApiServiceFactory = [
    '$http',
    '$q',
    function ($http, $q) {
        return new ThreadsApiServiceImpl($http, $q);
    },
];
